import React from "react";
import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import { statusOf } from "@/lib/baselineStatus";
import { TargetControl } from "@/components/energy/TargetControl";

const ICONS = { up: TrendingUp, down: TrendingDown, flat: Minus };

function UsageBars({ row, max, color }) {
  const pct = (v) => (max > 0 && v != null ? Math.max((v / max) * 100, 1) : 0);
  return (
    <div className="mt-3 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="k-sublabel w-16 shrink-0">Current</span>
        <div className="flex-1 h-2 rounded-full bg-[#F3F4F6] overflow-hidden relative">
          <div className="h-full rounded-full" style={{ width: `${pct(row.current_kwh)}%`, background: color, transition: "width .5s ease" }} />
          {row.target_kwh != null && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-[#156F5B]"
              style={{ left: `${pct(row.target_kwh)}%` }}
              title={`Target ${row.target_kwh.toLocaleString()} kWh`}
            />
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="k-sublabel w-16 shrink-0">Baseline</span>
        <div className="flex-1 h-2 rounded-full bg-[#F3F4F6] overflow-hidden">
          <div className="h-full rounded-full bg-[#CBD5D1]" style={{ width: `${pct(row.baseline_kwh)}%`, transition: "width .5s ease" }} />
        </div>
      </div>
    </div>
  );
}

function TargetProgress({ row }) {
  return (
    <div className="flex items-center gap-2" data-testid={`baseline-target-progress-${row.site_id}`}>
      <span className="k-sublabel">Target progress</span>
      <div className="w-28 h-2 rounded-full bg-[#F3F4F6] overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.min(row.target_progress_pct, 100)}%`,
            background: row.target_met ? "#156F5B" : "#D97706",
            transition: "width .5s ease",
          }}
        />
      </div>
      <span className="text-xs font-heading font-semibold text-[#4D4D4D]">{row.target_progress_pct}%</span>
    </div>
  );
}

export function BaselineRow({ row, max, isAdmin, onSaved }) {
  const st = statusOf(row.status);
  const Icon = ICONS[st.icon];
  return (
    <div className="p-5 border-b border-[#F0F2F1] last:border-0" data-testid={`baseline-row-${row.site_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#212121] truncate">{row.site_dis}</div>
          <div className="text-xs text-[#6B7280] mt-0.5">
            {row.current_kwh != null ? row.current_kwh.toLocaleString() : "—"} {row.unit} this period
            <span className="mx-1.5">vs</span>
            {row.baseline_kwh != null ? row.baseline_kwh.toLocaleString() : "—"} {row.unit} baseline
            {row.cumulative_meter && (
              <span className="ml-1.5 text-[#9CA3AF]" data-testid={`baseline-cumulative-note-${row.site_id}`}>
                · running-total meter, converted to usage
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-lg font-heading font-semibold" style={{ color: st.bar }} data-testid={`baseline-delta-${row.site_id}`}>
            {row.delta_pct != null ? `${row.delta_pct > 0 ? "+" : ""}${row.delta_pct}%` : "—"}
          </span>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-heading font-semibold ${st.cls}`}>
            <Icon className="h-3 w-3" /> {st.label}
          </span>
        </div>
      </div>

      <UsageBars row={row} max={max} color={st.bar} />

      <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
        <TargetControl row={row} isAdmin={isAdmin} onSaved={onSaved} />
        {row.target_progress_pct != null && <TargetProgress row={row} />}
      </div>
    </div>
  );
}
